import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
  IconButton,
  Avatar,
  AvatarGroup,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Event,
  People,
  School,
  AttachMoney,
  CalendarToday,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  NotificationsActive,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import AppSnackbar from "../Components/Snackbar/snackbar";
import apiEndpoints from "../apiconfig";

const AdminDashboard = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const [stats, setStats] = useState([]);
  const [events, setEvents] = useState([]);
  const [admissionData, setAdmissionData] = useState([]);
  const [feeData, setFeeData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [teacherLeaderboard, setTeacherLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceProgress, setAttendanceProgress] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(apiEndpoints.dashboard, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Dashboard API Response:", data);

        if (!data || data.status !== true) {
          setLoading(false);
          return;
        }

        const statsData = data.stats || {};

        setStats([
          {
            label: "Total Students",
            value: statsData.students ?? 0,
            change: "+0%",
            trend: "up",
            icon: <People />,
            gradient: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
          },
          {
            label: "Total Teachers",
            value: statsData.teachers ?? 0,
            change: "+0%",
            trend: "up",
            icon: <School />,
            gradient: "linear-gradient(135deg,#f093fb 0%,#f5576c 100%)",
          },
          {
            label: "Monthly Collection",
            value: `₹ ${(statsData.collection ?? 0).toLocaleString()}`,
            change: "+0%",
            trend: "up",
            icon: <AttachMoney />,
            gradient: "linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)",
          },
          {
            label: "Attendance",
            value: `${statsData.attendance ?? 0}%`,
            change: "0%",
            trend: "up",
            icon: <CalendarToday />,
            gradient: "linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)",
          },
        ]);

        setEvents(data.events ?? []);
        setAdmissionData(data.admissionTrend ?? []);
        setAttendanceData(data.attendanceByClass ?? []);
        setAttendanceProgress(data.attendanceProgress ?? []);
        setTeacherLeaderboard(data.topTeachers ?? []);

        setFeeData(
          (data.feeData ?? []).map((f) => ({
            name: f.name,
            amount: Number(f.amount) || 0,
            color:
              f.name === "Paid"
                ? "#4CAF50"
                : f.name === "Pending"
                  ? "#FF9800"
                  : "#F44336",
          })),
        );

        setStaffAttendance(
          (data.staffAttendance ?? []).map((s) => ({
            name: s.status,
            value: Number(s.count) || 0,
            color:
              s.status === "Present"
                ? "#4CAF50"
                : s.status === "Late"
                  ? "#FF9800"
                  : s.status === "Leave"
                    ? "#03A9F4"
                    : "#F44336",
          })),
        );

        setLoading(false);
      })
      .catch((err) => {
        console.error("Dashboard API Error:", err);
        setLoading(false);
      });
  }, []);

  const inventoryData = [
    { name: "Pink Tower", usage: 90, available: 10 },
    { name: "Bead Frames", usage: 75, available: 25 },
    { name: "Number Rods", usage: 60, available: 40 },
    { name: "Sandpaper Letters", usage: 82, available: 18 },
    { name: "Golden Beads", usage: 95, available: 5 },
    { name: "Colored Cylinders", usage: 70, available: 30 },
  ];

  //   const teacherPerformance = [
  //     { subject: "Math", score: 88 },
  //     { subject: "English", score: 92 },
  //     { subject: "Science", score: 85 },
  //     { subject: "Art", score: 78 },
  //     { subject: "PE", score: 95 },
  //   ];

  // Custom tooltip styles
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };
  const TeacherPerformance = () => (
    <Paper
      sx={{
        mt: 4,
        p: 3,
        borderRadius: 3,
        background: "rgba(255, 255, 255, 0.5)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.5)",
        boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        👑 Top Performing Teachers
      </Typography>

      {teacherLeaderboard.map((t, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ width: "30%", fontWeight: 700 }}>
            {i + 1}. {t.name}
          </Typography>

          <Typography sx={{ width: "25%", color: "text.secondary" }}>
            {t.subject}
          </Typography>

          <Box sx={{ width: "40%" }}>
            <LinearProgress
              variant="determinate"
              value={t.score}
              sx={{
                height: 7,
                borderRadius: 3,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                },
              }}
            />
          </Box>

          <Typography sx={{ fontWeight: 700 }}>{t.score}%</Typography>
        </Box>
      ))}
    </Paper>
  );
  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* HEADER SECTION */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #3F51B5 0%, #2196F3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Admin Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Welcome back! Here's what's happening today.
          </Typography>
        </Box>
      </Box>

      {/* STATS GRID WITH ENHANCED CARDS */}
      {/* =================== STAT CARDS FIXED SIZE =================== */}
      <Grid container spacing={3} sx={{ mb: 5, display: "flex" }}>
        {stats.map((stat, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            key={index}
            sx={{ display: "flex" }}
          >
            <Paper
              sx={{
                flex: 1,
                p: { xs: 2, sm: 2.5, md: 3 },
                borderRadius: "18px",
                background: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(14px)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 8px 22px rgba(0,0,0,.12)",
                overflow: "hidden",
                transition: "0.3s",
                minHeight: 150, // EVEN HEIGHT FIX
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // BALANCED CONTENT

                "&:hover": { transform: "translateY(-6px)" },
              }}
            >
              {/* ICON + LABEL */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    minWidth: 36,
                    borderRadius: "10px",
                    background: stat.gradient,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& svg": { fontSize: "18px" }, // SMALL ICON
                  }}
                >
                  {stat.icon}
                </Box>

                <Typography
                  sx={{ fontWeight: 600, opacity: 0.85, fontSize: "0.95rem" }}
                >
                  {stat.label}
                </Typography>
              </Box>

              {/* VALUE + TREND */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{ fontWeight: 800, fontSize: "2.1rem", mb: 0.3 }}
                >
                  {stat.value}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  {stat.trend === "up" ? (
                    <ArrowUpward sx={{ fontSize: 15, color: "success.main" }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: 15, color: "error.main" }} />
                  )}

                  <Typography
                    sx={{ fontSize: "0.85rem", fontWeight: 600, opacity: 0.9 }}
                  >
                    {stat.change} this month
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* MAIN CONTENT GRID */}
      <Grid container spacing={3} alignItems="stretch">
        {/* UPCOMING EVENTS - LEFT COLUMN */}
        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
              minHeight: { xs: "auto", lg: 680 },
              maxHeight: { xs: "auto", lg: 780 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Event sx={{ color: "primary.main" }} /> Upcoming Events
              </Typography>
              <Chip label="5 Events" size="small" color="primary" />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* SCROLLABLE AREA */}
            <Box
              sx={{
                overflowY: "auto",
                flexGrow: 1, // makes list fill available height

                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,.4)",
                  borderRadius: "20px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "rgba(0,0,0,.6)",
                },
              }}
            >
              {events.map((event, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.5)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 5px 15px rgba(15,23,42,.12)",
                    "&:hover": {
                      bgcolor: "action.hover",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {event.name}
                    </Typography>
                    <Chip label={event.date} size="small" />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Chip label={event.type} size="small" variant="outlined" />
                    {event.attendees && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {event.attendees} attending
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* CHARTS - RIGHT COLUMN */}
        <Grid item xs={12} lg={8}>
          {/* ADMISSION TREND CHART */}
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              mb: 3,
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Admission Trend
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Weekly student admissions vs targets
                </Typography>
              </Box>
              <Chip
                icon={<TrendingUp sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                label="+15% Growth"
                color="success"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "10px", sm: "14px" }, // reduce text size for 320px
                  px: { xs: 1, sm: 2 }, // smaller padding for small screens
                  height: { xs: 26, sm: 32 }, // prevents overflow
                  "& .MuiChip-label": {
                    px: { xs: 0.5, sm: 1.5 }, // fix inner label spacing
                  },
                }}
              />
            </Box>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={admissionData}>
                <defs>
                  <linearGradient
                    id="colorStudents"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3F51B5" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3F51B5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF9800" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#FF9800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="students"
                  stroke="#3F51B5"
                  fillOpacity={1}
                  fill="url(#colorStudents)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="target"
                  stroke="#FF9800"
                  fillOpacity={1}
                  fill="url(#colorTarget)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>

          {/* FEE COLLECTION & ATTENDANCE ROW */}
          <Grid container spacing={3}>
            {/* FEE COLLECTION PIE CHART */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Fee Collection Status
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={feeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="amount"
                    >
                      {feeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        `₹ ${value.toLocaleString()}`,
                        "Amount",
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  {feeData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: item.color,
                          }}
                        />
                        <Typography variant="body2">{item.name}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹ {item.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* ATTENDANCE BY CLASS */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Attendance by Class
                </Typography>
                <Box sx={{ height: 200, overflow: "auto", pr: 1 }}>
                  {attendanceData.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.class}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "success.main",
                            }}
                          >
                            {item.percent}%
                          </Typography>
                          {item.percent > 70 ? (
                            <TrendingUp
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          ) : (
                            <TrendingDown
                              sx={{ fontSize: 16, color: "error.main" }}
                            />
                          )}
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.percent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "action.disabledBackground",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background: `linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)`,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* BOTTOM CHARTS ROW */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* STUDENT PROGRESS RADAR CHART */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
              minHeight: { md: 400, lg: 420 }, // same height for both cards on large screens
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Student Progress by Monthly Attendance
              </Typography>
            </Box>
            {attendanceProgress.length < 3 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={attendanceProgress}>
                  <XAxis dataKey="label" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#3F51B5" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <RadarChart data={attendanceProgress}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="label" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar
                    name="Attendance %"
                    dataKey="value"
                    stroke="#3F51B5"
                    fill="#3F51B5"
                    fillOpacity={0.6}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* STAFF ATTENDANCE */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
              height: { md: 410, lg: 420 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Staff Attendance (This Month)
              </Typography>
              <Chip
                label="Monthly Summary"
                size="small"
                color="success"
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                    paddingY: 0.5,
                    textAlign: "center",
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.75rem",
                    },
                  },
                }}
              />
            </Box>

            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={staffAttendance}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  paddingAngle={5}
                >
                  {staffAttendance.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>

            {/* Legend */}
            <Box sx={{ mt: 2 }}>
              {staffAttendance.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: item.color,
                      }}
                    />
                    <Typography variant="body2">{item.name}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* TEACHER PERFORMANCE */}
      {/*<TeacherPerformance />*/}
      {/* Snackbar (uncomment when needed) */}
      {/* <AppSnackbar
        message="Login Successful"
        severity="success"
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      /> */}
    </Box>
  );
};

export default AdminDashboard;
